/* `/plans/buddy`: board M's page, its 56px header over the stage the conversation fills.
 *
 * `height: 100dvh` and `flex: none`, like the plan builder (see `PlanBuilderScreen.module.css` for
 * the measurement): the shell sizes itself with a `min-height`, which is not a definite height, so
 * the thread would otherwise grow the page instead of scrolling inside itself. */

.page {
  display: flex;
  flex: none;
  flex-direction: column;
  height: 100dvh;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: var(--bg);
  font-variant-numeric: tabular-nums;
}

.header {
  display: flex;
  flex: none;
  gap: var(--space-3);
  align-items: center;
  height: 56px;
  padding: var(--space-0) var(--space-20px);
  border-bottom: var(--border-hairline) solid var(--border);
}

.title {
  margin: var(--space-0);
  overflow: hidden;
  font-size: var(--fs-foot);
  font-weight: var(--fw-extrabold);
  color: var(--text);
  text-overflow: ellipsis;
}

/* A landing for focus after the conversation is deleted, never a tab stop. */
.title:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-2px);
}

.spacer {
  flex: 1;
}

.note {
  flex: none;
  margin: var(--space-0);
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

/* Everything under the header: the history rail, and the conversation beside it. */
.body {
  position: relative;
  display: flex;
  flex: 1;
  min-width: 0;
  min-height: 0;
}

/* The rail's column. It opens and closes at once, never by a width transition (that would lay the
 * conversation out again on every frame, the navigation panel's rule); the rail fades in instead. */
.railColumn {
  display: flex;
  flex: none;
  min-height: 0;
  animation-name: rail-in;
  animation-duration: var(--dur-base);
  animation-timing-function: var(--ease);
}

/* The rail while the session panel is open: out of the flow, so the conversation takes its column,
 * and hidden rather than unmounted, so its list keeps its scroll. */
.railBesideDock {
  position: absolute;
  inset-block: 0;
  inset-inline-start: 0;
  display: flex;
  visibility: hidden;
}

/* Closed by the header's toggle: still mounted, so the list keeps its scroll. */
.railColumn[hidden],
.railBesideDock[hidden] {
  display: none;
}

@keyframes rail-in {
  from {
    opacity: 0;
    transform: translateX(var(--space-minus-8px));
  }

  to {
    opacity: 1;
    transform: none;
  }
}

/* The conversation, or the state that stands in its place. */
.stage {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

/* The session panel is the builder's fixed 574px box at the page's end: the conversation gives it
 * that room rather than running under it. */
.stageBesideDock {
  padding-inline-end: var(--w-builder-panel);
}

/* „Plan dla: Anna Nowak": who the conversation's plan is for, a quiet line over the thread. */
.recipient {
  flex: none;
  margin: var(--space-0);
  padding: var(--space-3) var(--space-20px) var(--space-0);
  overflow: hidden;
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Buddy switched off, or the reads failed: one centred state where the hero would stand. */
.state {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-3);
  align-items: center;
  justify-content: center;
  padding: var(--space-6);
}

.stateMark {
  width: 44px;
  height: 44px;
}

/* „Usuń rozmowę": the ghost's treatment at the back button's 32px, so the header's two controls
 * stand on one line. Neutral: deleting is asked again in the dialog, where the danger fill is. */
.delete {
  height: 32px;
  padding: var(--space-0) var(--space-3);
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  background: transparent;
  border-radius: var(--radius-mark);
}

/* The history rail's sidebar toggle, top left where the rail opens: the navigation's own collapse
 * control, at the delete button's 32px so the header's controls stand on one line. */
.railToggle {
  display: grid;
  flex: none;
  place-items: center;
  width: 32px;
  height: 32px;
  margin-left: var(--space-minus-6px);
  color: var(--text-2);
  cursor: pointer;
  background: transparent;
  border: none;
  border-radius: var(--radius-mark);
  transition: background-color var(--dur-fast) var(--ease), color var(--dur-fast) var(--ease);
}

.railToggle:hover {
  color: var(--text);
  background: var(--hover-tint);
}

.railToggle:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-2px);
}

.railToggleIcon {
  display: block;
  width: var(--icon-sm);
  height: var(--icon-sm);
}

/* Its place kept while there is nothing to delete, so the note beside it never moves. */
.deleteReserved {
  visibility: hidden;
}
